import { useEffect, useState, useRef } from 'react';
import * as styles from './styles/hubspot-form.module.scss';
import Image from '@klv/atoms/image';
import { combineGclidAndGaIdValue } from '@klv/shared/utils/google-tag-manager';
import { generateClassName, getUrlTrackingParams } from '@klv/shared/utils/functions';
import { getStoredTrackingParams } from '@klv/shared/utils/attribution';
import getVisitorGeolocation from '@klv/context/localization/utils';

import type { IHubspotFormAtom } from './types';

const MAX_RETRIES = 20;
const FORM_ID_PREFIX = 'hubspot-form-';

const HubspotFormAtom = ({
  hubspotHeaderText,
  hubspotSubheaderText,
  formId,
  image,
}: IHubspotFormAtom) => {
  const uniqueIdRef = useRef<string>('');
  const intervalId = useRef<number>(0);
  const retryCount = useRef(0);

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    uniqueIdRef.current = `${FORM_ID_PREFIX}${crypto.randomUUID()}`;
  }, []);

  useEffect(() => {
    if (isLoaded && uniqueIdRef.current && formId) {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: process.env.GATSBY_HUBSPOT_TRACKING_ID || '',
          formId: formId,
          region: 'na1',
          target: `#${uniqueIdRef.current}`,
          // @ts-ignore - onFormReady is part of the hubspot create function
          onFormReady: async function (form) {
            const urlTrackingParams = getUrlTrackingParams();
            const storedTrackingParams = getStoredTrackingParams();
            const googleClientId = await combineGclidAndGaIdValue(urlTrackingParams?.gclid);
            const irclickid = urlTrackingParams?.irclickid || storedTrackingParams?.irclickid || '';
            const gclid = `${googleClientId}${irclickid ? `:${irclickid}` : ''}`;
            // Set the UTM parameters to hidden fields in the form
            Object.entries(storedTrackingParams).forEach(([key, value]) => {
              if (value) {
                const input = form.querySelector(`input[name="${key}"]`) as HTMLInputElement;
                if (input) {
                  input.value = value;
                }
              }
            });
            // Set gclid field
            const gclidInput = form.querySelector('input[name="gclid"]') as HTMLInputElement;
            if (gclidInput) {
              gclidInput.value = gclid;
            }

            // Set conversion page and web pages visited
            const conversionPageInput = form.querySelector('input[name="conversion_page"]') as HTMLInputElement;
            if (conversionPageInput) {
              conversionPageInput.value = window.location.href;
            }

            const webPagesVisitedInput = form.querySelector('input[name="web_pages_visited_prior_to_conversion"]') as HTMLInputElement;
            if (webPagesVisitedInput) {
              const klaviyoPagesVisitCount = sessionStorage.getItem('klaviyoPagesVisitCount');
              if (klaviyoPagesVisitCount) {
                try {
                  const pagesArray = JSON.parse(klaviyoPagesVisitCount);
                  webPagesVisitedInput.value = JSON.stringify(pagesArray);
                } catch (error) {
                  console.error('Error parsing klaviyoPagesVisitCount:', error);
                }
              }
            }

            // Set Location
            const locationInput = form.querySelector('input[name="country_indicated_at_signup"]') as HTMLInputElement;
            if (locationInput) {
              const locationData = await getVisitorGeolocation();
              if (locationData && locationData.country) {
                locationInput.value = locationData.country;
              }
            }
          },
        });
      } else {
        console.error('window.hbspt does not exist.');
      }
    }
  }, [formId, isLoaded]);

  useEffect(() => {
    intervalId.current = window.setInterval(() => {
      const hubspotFormsScript = document.getElementById('hs-script-forms');
      if (hubspotFormsScript) {
        setIsLoaded(true);
        window.clearInterval(intervalId.current);
      }
      retryCount.current += 1;
      if (retryCount.current >= MAX_RETRIES) {
        console.error('Hubspot forms script not loaded after maximum retries.');
        window.clearInterval(intervalId.current);
      }
    }, 250);

    return () => window.clearInterval(intervalId.current);
  }, []);

  return (
    <div className={styles.gridContainer}>
      <div>
        {hubspotHeaderText && <h4 className={generateClassName(['h4', 'semibold', styles.headline])}>{hubspotHeaderText}</h4>}
        {hubspotSubheaderText && <h6 className={generateClassName([styles.subHeadline])}>{hubspotSubheaderText}</h6>}
        <div className={styles.hubspotForm} id={uniqueIdRef.current} />
      </div>
      {image?.gatsbyImage && <Image altText={image?.altText} imageData={image?.gatsbyImage} />}
    </div>
  );
};

export default HubspotFormAtom;
